/**
 * useTenantConfig — thin accessor over useAuthStore().tenant.
 *
 * Provides typed helpers so components don't need to import the full
 * TenantConfig interface or guard against null everywhere.
 */
import { useAuthStore } from '../stores/auth';
import type { TenantConfig } from '../stores/auth';

/**
 * LBR2-G1 tenant-gating — pure capability selector. Missing tenant or missing
 * `capabilities` (older backend response) → false (fail-closed). Exported for
 * unit testing and for use outside React (e.g. nav filtering).
 */
export function hasCapability(
  tenant: TenantConfig | null | undefined,
  key: 'discountUi' | 'recruitment',
): boolean {
  return tenant?.capabilities?.[key] === true;
}

export function useTenantConfig() {
  const tenant = useAuthStore((s) => s.tenant);

  return {
    /** Raw tenant config — may be null before auth resolves. */
    tenant,

    /** Custom AI assistant name, or "Kiaan" if unset. */
    aiAssistantName: tenant?.aiAssistantName || 'Kiaan',

    /** Whether the current tenant is on the ENTERPRISE plan. */
    isEnterprise: tenant?.planTier === 'ENTERPRISE',

    /** Enabled modules list. */
    enabledModules: tenant?.enabledModules ?? null,

    /** Plan tier string. */
    planTier: tenant?.planTier ?? null,

    /** LBR2-G1 — show the discount-slab UI surfaces (SS-only by default). */
    canDiscountUi: hasCapability(tenant, 'discountUi'),

    /** LBR2-G1 — show HR-recruitment surfaces (SS-only by default). */
    canRecruitment: hasCapability(tenant, 'recruitment'),
  };
}
